import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import store from 'store';
import getDashboardApiUrl from '@/utils/getDashboardApiUrl';

export type WebhookEventType =
  | 'message.inbound'
  | 'message.outbound'
  | 'review.received'
  | 'review.updated'
  | 'review.responded'
  | 'review_invitation.sent'
  | 'pipeline_stage.changed';

export type WebhookTestEventType = Exclude<WebhookEventType, 'message.outbound' | 'review.updated'>;

export interface WebhookEndpoint {
  _id: string;
  endpointUrl: string;
  subscribedEvents: WebhookEventType[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDelivery {
  deliveryId: string;
  eventType: WebhookEventType;
  test: boolean;
  success: boolean;
  attempts: number;
  statusCode: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface WebhookEndpointsResponse {
  webhookEndpoints: WebhookEndpoint[];
  maxEndpoints: number;
}

export interface WebhookEndpointFields {
  endpointUrl?: string;
  subscribedEvents?: WebhookEventType[];
}

export interface CreateWebhookEndpointResponse {
  webhookEndpoint: WebhookEndpoint;
  webhookSecret: string;
}

export interface WebhookTestResult {
  success: boolean;
  statusCode: number | null;
  message: string;
}

const ENDPOINTS_TAG = { type: 'WebhookEndpoint' as const, id: 'LIST' };
const deliveriesTag = (_id: string) => ({ type: 'WebhookDelivery' as const, id: _id });
const endpointUrl = (_id: string, path = '') => `/api/webhook-endpoints/${_id}${path}`;

export const webhookEndpointApi = createApi({
  reducerPath: 'webhookEndpointApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getDashboardApiUrl(window.location.href),
    prepareHeaders: (headers) => {
      const token = store.get('jwt');
      if (token) {
        headers.set('Authorization', `JWT ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['WebhookEndpoint', 'WebhookDelivery'],
  endpoints: (builder) => ({
    getWebhookEndpoints: builder.query<WebhookEndpointsResponse, void>({
      query: () => ({ url: '/api/webhook-endpoints' }),
      providesTags: [ENDPOINTS_TAG],
    }),
    createWebhookEndpoint: builder.mutation<CreateWebhookEndpointResponse, WebhookEndpointFields>({
      query: (body) => ({ url: '/api/webhook-endpoints', method: 'POST', body }),
      invalidatesTags: [ENDPOINTS_TAG],
    }),
    updateWebhookEndpoint: builder.mutation<
      { webhookEndpoint: WebhookEndpoint },
      WebhookEndpointFields & { _id: string }
    >({
      query: ({ _id, ...body }) => ({ url: endpointUrl(_id), method: 'PATCH', body }),
      invalidatesTags: [ENDPOINTS_TAG],
    }),
    removeWebhookEndpoint: builder.mutation<{ deleted: boolean }, string>({
      query: (_id) => ({ url: endpointUrl(_id), method: 'DELETE' }),
      invalidatesTags: [ENDPOINTS_TAG],
    }),
    revealWebhookSecret: builder.mutation<{ webhookSecret: string }, string>({
      query: (_id) => ({ url: endpointUrl(_id, '/secret'), method: 'POST' }),
    }),
    rotateWebhookSecret: builder.mutation<{ webhookSecret: string }, string>({
      query: (_id) => ({ url: endpointUrl(_id, '/rotate-secret'), method: 'POST' }),
    }),
    getWebhookDeliveries: builder.query<{ deliveries: WebhookDelivery[] }, string>({
      query: (_id) => ({ url: endpointUrl(_id, '/deliveries') }),
      providesTags: (_result, _error, _id) => [deliveriesTag(_id)],
    }),
    sendTestWebhook: builder.mutation<
      WebhookTestResult,
      { _id: string; eventType: WebhookTestEventType }
    >({
      query: ({ _id, eventType }) => ({
        url: endpointUrl(_id, '/test'),
        method: 'POST',
        body: { eventType },
      }),
      invalidatesTags: (_result, _error, { _id }) => [deliveriesTag(_id)],
    }),
  }),
});

export const {
  useGetWebhookEndpointsQuery,
  useCreateWebhookEndpointMutation,
  useUpdateWebhookEndpointMutation,
  useRemoveWebhookEndpointMutation,
  useRevealWebhookSecretMutation,
  useRotateWebhookSecretMutation,
  useGetWebhookDeliveriesQuery,
  useSendTestWebhookMutation,
} = webhookEndpointApi;
